import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';
import LoadingScreen from './components/layout/LoadingScreen';
import ScrollProgress from './components/layout/ScrollProgress';
import Navbar from './components/layout/Navbar';
import Hero from './components/sections/Hero';
import AboutTeam from './components/sections/AboutTeam';
import TeamMembers from './components/sections/TeamMembers';
import Skills from './components/sections/Skills';
import Projects from './components/sections/Projects';
import Contact from './components/sections/Contact';
import VantaBirdsWrapper from './components/ui/VantaBirdsWrapper';
import Footer from './components/layout/Footer';
import { SnippetPage } from './features/snippet';
import { parseSnippetIdFromHash } from './features/snippet/snippet.utils';

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showSnippet, setShowSnippet] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      // Auto-open SnippetPage if URL hash contains a snippet ID
      if (parseSnippetIdFromHash(window.location.hash)) {
        setShowSnippet(true);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleOpenSnippet = useCallback(() => {
    setShowSnippet(true);
  }, []);

  const handleCloseSnippet = useCallback(() => {
    setShowSnippet(false);
    // Clear the snippet hash from URL when closing
    if (parseSnippetIdFromHash(window.location.hash)) {
      window.history.pushState('', document.title, window.location.pathname + window.location.search);
    }
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
          <LoadingScreen isVisible={isLoading} />
          <a href="#main-content" className="skip-to-content">
            Skip to content
          </a>
          {!isLoading && <ScrollProgress />}
          {!isLoading && <Navbar onOpenSnippet={handleOpenSnippet} />}
          <main id="main-content">
            <Hero />
            <AboutTeam />
            <TeamMembers />
            <Skills />
            <VantaBirdsWrapper>
              <Projects />
              <Contact />
            </VantaBirdsWrapper>
          </main>
          <Footer />
          {/* Snippet overlay — rendered outside main, above navbar */}
          <AnimatePresence>
            {showSnippet && (
              <SnippetPage onClose={handleCloseSnippet} />
            )}
          </AnimatePresence>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;
